import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import deductionConfig from './configuration/deductionConfig.json';
import { firestore } from '../../components/firebase.config';

const WALLET_DOC_ID = 'primary';
const WALLET_COLLECTION = 'wallet';
const TRANSACTION_COLLECTION = 'transactions';
const DEFAULT_EXPIRY_DAYS = 30; // how long a wallet stays valid before it needs topping up again
const DEFAULT_LICENSE_TYPE = 'Free';
const LICENSE_TYPES = ['Free', 'Pro', 'Enterprise'];

const getWalletDocRef = (tenantId) =>
  doc(firestore, 'Tenants', tenantId, WALLET_COLLECTION, WALLET_DOC_ID);
// ─── Expiry Calculation (hour-precise) ─────────────────────────────────────
// Expiry is always calculated as (days × 24 hours) counted exactly from the
// moment the action happens — wallet creation or top-up — not rounded to a
// calendar date. This keeps the 24-hour period intact for every plan:
// Free = 15 × 24h, Pro = 30 × 24h, Enterprise = 365 × 24h, all measured from
// the precise timestamp of the top-up/creation event.
const HOURS_PER_DAY = 24;
const MS_PER_HOUR = 60 * 60 * 1000;

const calculateExpiryDate = (days, fromDate = new Date()) => {
  const totalHours = days * HOURS_PER_DAY;
  return Timestamp.fromDate(new Date(fromDate.getTime() + totalHours * MS_PER_HOUR));
};
export const getWalletConfig = () => deductionConfig;

export const getDeductionValue = (appName, action) => {
  const normalizedAppName = (appName || '').toLowerCase();
  const actionConfig = deductionConfig?.[normalizedAppName]?.[action];
  return actionConfig?.points ?? null;
};

export const getLicenseConfig = (appName, licenseType) => {
  const normalizedAppName = (appName || '').toLowerCase();
  return deductionConfig?.[normalizedAppName]?.licenses?.[licenseType] || null;
};

export const isActionAllowed = (appName, action, hasBalance) => {
  const normalizedAppName = (appName || '').toLowerCase();
  const actionConfig = deductionConfig?.[normalizedAppName]?.[action];
  if (!actionConfig) return false;
  if (hasBalance) return true;
  return !!actionConfig.allowedOnInsufficientBalance;
};

export const ensureWallet = async (tenantId, defaults = {}) => {
  if (!tenantId) return null;

  const walletRef = getWalletDocRef(tenantId);
  const snapshot = await getDoc(walletRef);

  if (snapshot.exists()) {
    return snapshot.data();
  }

  const licenseType = defaults.licenseType || DEFAULT_LICENSE_TYPE;
  const appName = defaults.appName || 'cbam';
  const licenseConfig = getLicenseConfig(appName, licenseType) || {};
  const walletNumber = defaults.walletNumber || `WLT-${String(tenantId).slice(0, 8).toUpperCase()}`;

  const initialBalance = defaults.balance ?? licenseConfig.defaultCredits ?? 0;

  // Free/Pro get an auto-calculated expiry. Enterprise has no automatic expiry —
  // it's contractual, set explicitly via options.expiryDate when topping up.
  const expiryDate = licenseConfig.expiryDays
    ? calculateExpiryDate(licenseConfig.expiryDays)
    : (defaults.expiryDate ? Timestamp.fromDate(new Date(defaults.expiryDate)) : null);

  const walletPayload = {
    tenantId,
    walletNumber,
    balance: initialBalance,
    totalAllocated: initialBalance, // running total ever credited — used to show "X used of Y"
    expiryDate,
    licenseType,
    submissionsUsed: 0,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(walletRef, walletPayload);
  return walletPayload;
};

export const getWalletSummary = async (tenantId) => {
  if (!tenantId) return null;

  const walletRef = getWalletDocRef(tenantId);
  const snapshot = await getDoc(walletRef);

  if (!snapshot.exists()) {
    return {
      wallet: null,
      transactions: [],
    };
  }

  const transactionsRef = collection(walletRef, TRANSACTION_COLLECTION);
  const txSnapshot = await getDocs(transactionsRef);
  const transactions = txSnapshot.docs
    .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
    .sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return bTime - aTime;
    });

  return {
    wallet: snapshot.data(),
    transactions,
  };
};

export const getWalletPermissions = async (tenantId, appName = 'cbam') => {
  if (!tenantId) return {
    canRead: false,
    canAdd: false,
    canUpdate: false,
    canDelete: false,
    reason: 'Tenant not available',
    wallet: null,
  };

  const wallet = await ensureWallet(tenantId, { licenseType: DEFAULT_LICENSE_TYPE, appName });
  const balance = Number(wallet?.balance || 0);
  const expiry = wallet?.expiryDate ? (wallet.expiryDate.toDate ? wallet.expiryDate.toDate() : new Date(wallet.expiryDate)) : null;
  const now = Date.now();
  const expired = expiry ? now > expiry.getTime() : false;
  const hasBalance = balance > 0 && !expired;

  const reason = expired
    ? 'Wallet has expired. Please top up to restore full access.'
    : balance <= 0
    ? 'Wallet balance is insufficient. Limited access only.'
    : null;

  return {
    wallet,
    canRead: isActionAllowed(appName, 'read', hasBalance),
    canAdd: isActionAllowed(appName, 'add', hasBalance),
    canUpdate: isActionAllowed(appName, 'update', hasBalance),
    canDelete: isActionAllowed(appName, 'delete', hasBalance),
    reason,
  };
};

export const topUpWallet = async (tenantId, amount, options = {}) => {
  if (!tenantId) throw new Error('A tenant is required before topping up the wallet.');

  const numericAmount = Number(amount || 0);
  if (Number.isNaN(numericAmount) || numericAmount <= 0) {
    throw new Error('Top-up amount must be greater than zero.');
  }

  const walletRef = getWalletDocRef(tenantId);
  const existingWallet = await ensureWallet(tenantId, {
    licenseType: options.licenseType || DEFAULT_LICENSE_TYPE,
    appName: options.appName || 'cbam',
  });

  const licenseType = options.licenseType || existingWallet?.licenseType || DEFAULT_LICENSE_TYPE;
  const licenseConfig = getLicenseConfig(options.appName || 'cbam', licenseType) || {};

  // Free/Pro have a fixed minimum top-up. Enterprise is flexible — any amount
  // the contract specifies (₹50, ₹75, ₹240, etc.) is valid, no minimum enforced.
  if (!licenseConfig.flexibleTopUp && licenseConfig.minimumTopUp && numericAmount < licenseConfig.minimumTopUp) {
    throw new Error(`Minimum top-up for ${licenseType} is ${licenseConfig.minimumTopUp}.`);
  }

  const currentBalance = Number(existingWallet?.balance || 0);
  const nextBalance = currentBalance + numericAmount;
  // Track cumulative credits ever added, separate from current balance, so
  // the UI can show "X used of Y total" — falls back to currentBalance for
  // wallets created before this field existed.
  const currentTotalAllocated = Number(existingWallet?.totalAllocated ?? currentBalance);
  const nextTotalAllocated = currentTotalAllocated + numericAmount;

  // Expiry: Free = 15 days, Pro = 30 days (from licenseConfig). Enterprise has
  // no automatic expiry — only changes if an explicit expiryDate is passed in
  // (i.e. the contract terms), otherwise it stays whatever it already was.
  let expiryValue;
  if (options.expiryDate) {
    expiryValue = Timestamp.fromDate(new Date(options.expiryDate));
  } else if (licenseConfig.expiryDays) {
    expiryValue = calculateExpiryDate(licenseConfig.expiryDays);
  } else {
    expiryValue = existingWallet?.expiryDate || null;
  }

  const walletNumber = existingWallet?.walletNumber || `WLT-${String(tenantId).slice(0, 8).toUpperCase()}`;
  const updatePayload = {
    balance: nextBalance,
    totalAllocated: nextTotalAllocated,
    expiryDate: expiryValue,
    licenseType,
    walletNumber,
    updatedAt: serverTimestamp(),
  };

  if (options.resetSubmissionLimit === true) {
    updatePayload.submissionsUsed = 0;
  }

  await updateDoc(walletRef, updatePayload);

  const transactionPayload = {
    type: 'topup',
    appName: 'wallet',
    amount: numericAmount,
    balanceBefore: currentBalance,
    balanceAfter: nextBalance,
    description: options.description || 'Wallet top up',
    actor: options.actor || 'admin',
    walletNumber,
    createdAt: serverTimestamp(),
  };

  const transactionsRef = collection(walletRef, TRANSACTION_COLLECTION);
  await addDoc(transactionsRef, transactionPayload);

  return {
    walletNumber,
    balance: nextBalance,
    expiryDate: expiryValue,
    licenseType,
  };
};

export const applyWalletAction = async (tenantId, appName, action, options = {}) => {
  if (!tenantId) throw new Error('A tenant is required to apply wallet credits.');
  return consumeWalletPoints(tenantId, appName, action, options);
};

export const consumeWalletPoints = async (tenantId, appName, action, options = {}) => {
  if (!tenantId) throw new Error('A tenant is required to consume wallet credits.');
  // Resolve deduction value with fallbacks: appName -> 'all'
  let deductionValue = getDeductionValue(appName, action);
  if (deductionValue === null) {
    deductionValue = getDeductionValue('all', action);
  }

  if (deductionValue === null) {
    const err = new Error('No deduction mapping for this action');
    err.code = 'CONFIG_NOT_FOUND';
    throw err;
  }

  const walletRef = getWalletDocRef(tenantId);
  const existingWallet = await ensureWallet(tenantId, {
    licenseType: options.licenseType || DEFAULT_LICENSE_TYPE,
    appName,
  });

  // Check expiry
  if (existingWallet?.expiryDate) {
    const expiry = existingWallet.expiryDate.toDate ? existingWallet.expiryDate.toDate() : new Date(existingWallet.expiryDate);
    if (Date.now() > expiry.getTime()) {
      const err = new Error('Wallet has expired');
      err.code = 'WALLET_EXPIRED';
      throw err;
    }
  }

  const currentBalance = Number(existingWallet?.balance || 0);
  const nextBalance = currentBalance - deductionValue;

  if (nextBalance < 0) {
    const err = new Error('Wallet balance is insufficient for this action.');
    err.code = 'INSUFFICIENT_BALANCE';
    throw err;
  }

  await updateDoc(walletRef, {
    balance: nextBalance,
    updatedAt: serverTimestamp(),
  });

  const transactionPayload = {
    type: 'deduction',
    appName,
    action,
    deductionValue,
    balanceBefore: currentBalance,
    balanceAfter: nextBalance,
    description: options.description || `${action} action consumed wallet points`,
    actor: options.actor || 'system',
    walletNumber: existingWallet?.walletNumber || `WLT-${String(tenantId).slice(0, 8).toUpperCase()}`,
    createdAt: serverTimestamp(),
  };

  const transactionsRef = collection(walletRef, TRANSACTION_COLLECTION);
  await addDoc(transactionsRef, transactionPayload);

  return {
    deductionValue,
    balance: nextBalance,
  };
};

// ─── Submission Limits (Pro-tier report submission cap) ──────────────────

export const canSubmitReport = async (tenantId, appName = 'cbam') => {
  if (!tenantId) return { allowed: false, remaining: 0, reason: 'Tenant not available' };

  const wallet = await ensureWallet(tenantId, { licenseType: DEFAULT_LICENSE_TYPE, appName });
  const licenseConfig = getLicenseConfig(appName, wallet?.licenseType || DEFAULT_LICENSE_TYPE) || {};
  const limit = licenseConfig.submissionLimit; // null/undefined = unlimited; 0 = no free submissions
  const used = Number(wallet?.submissionsUsed || 0);

  if (limit === null || limit === undefined) {
    return { allowed: true, remaining: null, reason: null };
  }

  const normalizedLimit = Number(limit);
  const hasCapacity = Number.isFinite(normalizedLimit) && normalizedLimit > 0 && used < normalizedLimit;

  return {
    allowed: hasCapacity,
    remaining: Math.max(normalizedLimit - used, 0),
    reason: hasCapacity ? null : `Submission limit reached for ${wallet?.licenseType || DEFAULT_LICENSE_TYPE} plan (${normalizedLimit} max).`,
  };
};

export const recordSubmission = async (tenantId, appName = 'cbam') => {
  if (!tenantId) return { allowed: false, remaining: 0, reason: 'Tenant not available' };

  const check = await canSubmitReport(tenantId, appName);
  if (!check.allowed) {
    return check;
  }

  const walletRef = getWalletDocRef(tenantId);
  const snapshot = await getDoc(walletRef);
  const current = snapshot.exists() ? Number(snapshot.data().submissionsUsed || 0) : 0;
  await updateDoc(walletRef, { submissionsUsed: current + 1, updatedAt: serverTimestamp() });

  return {
    allowed: true,
    remaining: check.remaining === null ? null : Math.max(check.remaining - 1, 0),
    reason: null,
  };
};
